import { KM_PER_MILE, distanceBoth } from "./distance";
// Unit preference resolution + conversion/formatting helpers (settings-
// coverage pass, part 4, 2026-09-27). display.temperature_unit and
// display.distance_unit (GET /api/config/display, loaded once into
// AppData — see state/AppData.tsx's displaySettings) are each
// "auto" | an explicit unit; "auto" resolves from the browser's own
// locale.
//
// Auto convention (an engineering choice made explicit here, not a
// requirement pulled from any backend/protocol source): a locale whose
// REGION subtag is US, LR (Liberia), or MM (Myanmar) resolves to
// Fahrenheit + miles; every other locale resolves to Celsius + km. Those
// three are, per common reference (e.g. the CIA World Factbook's list of
// non-metric countries), the only countries that have not adopted the
// metric system as their official/primary system of measurement — so this
// is the same "imperial" set for both temperature and distance, not two
// independently-guessed lists.

export type TemperatureUnitPref = "auto" | "c" | "f";
export type DistanceUnitPref = "auto" | "km" | "mi";

const IMPERIAL_REGIONS = new Set(["US", "LR", "MM"]);

function localeRegion(tag: string): string | null {
  try {
    // Intl.Locale.maximize() fills in the region subtag even for a
    // language-only tag (e.g. "en" -> "en-Latn-US" on a US-configured
    // browser) — more reliable than a bare regex on the raw tag.
    const region = new Intl.Locale(tag).maximize().region;
    return region ?? null;
  } catch {
    const m = /-([a-zA-Z]{2})(?:-|$)/.exec(tag);
    return m ? m[1].toUpperCase() : null;
  }
}

function browserPrefersImperial(): boolean {
  if (typeof navigator === "undefined") return false;
  const candidates = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    if (!tag) continue;
    const region = localeRegion(tag);
    if (region) return IMPERIAL_REGIONS.has(region);
  }
  return false;
}

export function resolveTemperatureUnit(pref: TemperatureUnitPref | undefined): "c" | "f" {
  if (pref === "c" || pref === "f") return pref;
  return browserPrefersImperial() ? "f" : "c";
}

export function resolveDistanceUnit(pref: DistanceUnitPref | undefined): "km" | "mi" {
  if (pref === "km" || pref === "mi") return pref;
  return browserPrefersImperial() ? "mi" : "km";
}

export function celsiusToFahrenheit(c: number): number {
  return (c * 9) / 5 + 32;
}

export function fahrenheitToCelsius(f: number): number {
  return ((f - 32) * 5) / 9;
}


export function kmToMiles(km: number): number {
  return km / KM_PER_MILE;
}

export function milesToKm(mi: number): number {
  return mi * KM_PER_MILE;
}

// Formats a value that's stored/fetched in Celsius, in whichever unit
// `pref` resolves to ("auto" included). Digits defaults to 0 (matching
// the rounding NodeDetailPage's telemetry cards used before this pass).
export function formatTemperatureC(celsiusValue: number, pref: TemperatureUnitPref | undefined, digits = 0): string {
  const unit = resolveTemperatureUnit(pref);
  const value = unit === "f" ? celsiusToFahrenheit(celsiusValue) : celsiusValue;
  return `${value.toFixed(digits)}°${unit.toUpperCase()}`;
}

// Formats a value that's stored/fetched in kilometres, in whichever unit
// `pref` resolves to.
export function formatDistanceKm(kmValue: number, pref: DistanceUnitPref | undefined, digits = 0): string {
  const unit = resolveDistanceUnit(pref);
  const value = unit === "mi" ? kmToMiles(kmValue) : kmValue;
  return `${value.toFixed(digits)} ${unit}`;
}

// Same as formatDistanceKm, but the source value is already in miles (the
// shape a couple of existing API responses use — e.g. audit reachability's
// `furthest.miles`).
export function formatDistanceMiles(milesValue: number, pref: DistanceUnitPref | undefined, digits = 0): string {
  return formatDistanceKm(milesToKm(milesValue), pref, digits);
}

export function temperatureUnitLabel(pref: TemperatureUnitPref | undefined): string {
  return resolveTemperatureUnit(pref) === "f" ? "°F" : "°C";
}

export function distanceUnitLabel(pref: DistanceUnitPref | undefined): string {
  return resolveDistanceUnit(pref);
}

// A distance in both units, the display preference first (see lib/distance.ts):
// "122 mi (196 km)", or "196 km (122 mi)" when the preference is km. Source in km.
export function formatDistanceBoth(
  kmValue: number | null | undefined,
  pref: DistanceUnitPref | undefined,
  digits?: number,
  dash?: string,
): string {
  return distanceBoth(kmValue, resolveDistanceUnit(pref), digits, dash);
}
